<template>
    <div ref="menu" class="s-side-menu-control">
        <!-- ================= TABS OR LINKS ================= -->
        <template v-if="type === 'tabs' || type === 'links'">
            <ul class="s-side-menu-control__list">
                <template v-for="(group, gIndex) in listGroups" :key="group.id || gIndex">
                    <side-menu-group-header
                        :text="group.text"
                        :glyph="group.glyph"
                    />

                    <side-menu-list-item
                        v-for="item in group.items"
                        :key="item.id"
                        :tag="item.tag"
                        :label="item.label"
                        :href="item.href"
                        :class-name="getClassElement(item.raw)"
                        :show-counter="item.counter !== null"
                        :counter="item.counter"
                        @click="onChange(item.id)"
                    />
                </template>
            </ul>

            <el-select
                v-if="isMounted"
                class="s-side-menu-control__select"
                :model-value="activeElement"
                @change="onChange"
            >
                <template #selectSuffix>
                    <span class="s-side-menu-control__element-counter select-counter">
                        {{ hasCounter(activeItem) ? activeItem?.data?.cards?.length : '' }}
                    </span>
                </template>
                <template v-if="hasGroups">
                    <template
                        v-for="(group, gIndex) in groupedSlices"
                        :key="group.id || `g-${gIndex}`"
                    >
                        <el-option-group
                            v-if="group.text"
                            :label="group.text"
                            :disabled="Boolean(group.disabled)"
                        >
                            <el-option
                                v-for="item in group.items"
                                :key="getSliceId(item)"
                                :value="getSliceId(item)"
                                :label="item.info.caption"
                                :hide-on-resize="type === 'tabs' && isDesktop"
                            >
                                <template #suffix>
                                    <span
                                        v-if="hasCounter(item)"
                                        class="s-side-menu-control__element-counter select-counter"
                                    >
                                        {{ item.data.cards.length }}
                                    </span>
                                </template>
                            </el-option>
                        </el-option-group>

                        <template v-else>
                            <el-option
                                v-for="item in group.items"
                                :key="getSliceId(item)"
                                :value="getSliceId(item)"
                                :label="item.info.caption"
                                :hide-on-resize="type === 'tabs' && isDesktop"
                            >
                                <template #suffix>
                                    <span
                                        v-if="hasCounter(item)"
                                        class="s-side-menu-control__element-counter select-counter"
                                    >
                                        {{ item.data.cards.length }}
                                    </span>
                                </template>
                            </el-option>
                        </template>
                    </template>
                </template>

                <template v-else>
                    <el-option
                        v-for="item in flatSlices"
                        :key="getSliceId(item)"
                        :value="getSliceId(item)"
                        :label="item.info.caption"
                        :hide-on-resize="type === 'tabs' && isDesktop"
                    >
                        <template #suffix>
                            <span
                                v-if="hasCounter(item)"
                                class="s-side-menu-control__element-counter select-counter"
                            >
                                {{ item.data.cards.length }}
                            </span>
                        </template>
                    </el-option>
                </template>
            </el-select>
        </template>

        <!-- ================= COMBINED ================= -->
        <div v-else-if="type === 'combined'" class="s-side-menu-control__combined-wrapper">
            <el-select
                v-if="isMounted"
                class="s-side-menu-control__select"
                :model-value="combinedMobileValue"
                @change="onCombinedMobileChange"
            >
                <template #selectSuffix>
                    <span class="s-side-menu-control__element-counter select-counter">
                        {{ hasCounter(activeItem) ? activeItem?.data?.cards?.length : '' }}
                    </span>
                </template>
                <!-- with groups -->
                <template v-if="hasGroups">
                    <template
                        v-for="(group, gIndex) in combinedSelectGroups"
                        :key="group.id || `g-${gIndex}`"
                    >
                        <el-option-group
                            v-if="group.text"
                            :label="group.text"
                            :disabled="Boolean(group.disabled)"
                        >
                            <template v-for="parent in group.items" :key="getSliceId(parent)">
                                <!-- parent -->
                                <el-option :value="getSliceId(parent)" :label="parent.info.caption" />

                                <!-- children -->
                                <template v-if="showChildren(parent)">
                                    <el-option
                                        v-for="child in parent.slices || []"
                                        :key="getSliceId(child)"
                                        :value="getSliceId(child)"
                                        class="child-option"
                                        :label="child.info.caption"
                                    >
                                        <template #suffix>
                                            <span v-if="hasCounter(child)" class="s-side-menu-control__element-counter select-counter">
                                                {{ child.data.cards.length }}
                                            </span>
                                        </template>
                                    </el-option>
                                </template>
                            </template>
                        </el-option-group>

                        <!-- ungrouped (no title) -->
                        <template v-else>
                            <template v-for="parent in group.items" :key="getSliceId(parent)">
                                <el-option :value="getSliceId(parent)" :label="parent.info.caption" />
                                <template v-if="showChildren(parent)">
                                    <el-option
                                        v-for="child in parent.slices || []"
                                        :key="getSliceId(child)"
                                        :value="getSliceId(child)"
                                        class="child-option"
                                        :label="child.info.caption"
                                    >
                                        <template #suffix>
                                            <span v-if="hasCounter(child)" class="s-side-menu-control__element-counter select-counter">
                                                {{ child.data.cards.length }}
                                            </span>
                                        </template>
                                    </el-option>
                                </template>
                            </template>
                        </template>
                    </template>
                </template>

                <!-- no groups -->
                <template v-else>
                    <template v-for="parent in slices" :key="getSliceId(parent)">
                        <el-option :value="getSliceId(parent)" :label="parent.info.caption" />
                        <template v-if="showChildren(parent)">
                            <el-option
                                v-for="child in parent.slices || []"
                                :key="getSliceId(child)"
                                :value="getSliceId(child)"
                                class="child-option"
                                :label="child.info.caption"
                            >
                                <template #suffix>
                                    <span v-if="hasCounter(child)" class="s-side-menu-control__element-counter select-counter">
                                        {{ child.data.cards.length }}
                                    </span>
                                </template>
                            </el-option>
                        </template>
                    </template>
                </template>
            </el-select>

            <a-accordion one-only-mode class="s-side-menu-control__list">
                <template
                    v-for="(group, gIndex) in combinedSelectGroups"
                    :key="group.id || gIndex"
                >
                    <!-- group header (same look as tabs/links) -->
                    <side-menu-group-header
                        :text="group.text"
                        :glyph="group.glyph"
                    />

                    <a-accordion-item
                        v-for="item in group.items"
                        :key="getSliceId(item)"
                        @title-click="onChange(getSliceId(item))"
                    >
                        <template #title>
                            <span
                                class="s-side-menu-control__element a-accordion__title"
                                :class="getClassElement(item)"
                            >
                                <a-glyph
                                    v-if="item.slices?.length > 1"
                                    class="s-side-menu-control__arrow"
                                    name="drop-down"
                                />
                                {{ item.info.caption }}
                            </span>
                        </template>

                        <ul
                            v-if="item.slices?.length > 1 && !isAggregateCombined"
                            class="s-side-menu-control__sublist"
                        >
                            <li
                                v-for="child in item.slices"
                                :key="getSliceId(child)"
                                class="s-side-menu-control__subitem"
                            >
                                <button
                                    type="button"
                                    class="s-side-menu-control__link a-link"
                                    @click="onCombinedDesktopChildClick(item, child)"
                                >
                                    {{ child.info.caption }}
                                </button>
                            </li>
                        </ul>
                    </a-accordion-item>
                </template>
            </a-accordion>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import AAccordionItem from '@core/components/accordion/accordion-item.vue';
import AAccordion from '@core/components/accordion/accordion.vue';
import AGlyph from '@core/components/glyph/glyph.vue';
import ElOptionGroup from '@core/components/select/option-group.vue';
import ElOption from '@core/components/select/option.vue';
import ElSelect from '@core/components/select/select.vue';
import { useBreakpoint } from '@core/composables/useBreakpoint';
import { getElementPath } from '@utils/analytics.ts';
import SideMenuGroupHeader from './side-menu-group-header.vue';
import SideMenuListItem from './side-menu-list-item.vue';

defineOptions({ name: 'SSideMenuControl' });

const emit = defineEmits(['tab-change']);

const props = defineProps({
    activeElement: {
        type: String,
        required: true,
    },
    groups: {
        type: Array,
        default: () => [],
    },
    type: {
        type: String,
        required: true,
    },
    slices: {
        type: Array,
        required: true,
    },
});

const menu = ref(null);
const isMounted = ref(false);
const combinedMobileValue = ref('');

const { isDesktop } = useBreakpoint();

const hasGroups = computed(() => Array.isArray(props.groups) && props.groups.length > 0);
const groupedSlices = computed(() => {
    if (!hasGroups.value) return [{ id: null, items: props.slices }];

    const map = new Map();
    props.groups.forEach((group) => map.set(group.id, { ...group, items: [] }));

    const ungrouped = { id: '__ungrouped', text: null, glyph: null, items: [] };

    props.slices.forEach((slice) => {
        if (slice.groupId && map.has(slice.groupId)) {
            map.get(slice.groupId).items.push(slice);
        } else {
            ungrouped.items.push(slice);
        }
    });

    if (ungrouped.items.length) map.set('__ungrouped', ungrouped);
    return Array.from(map.values()).filter((g) => g.items?.length);
});

const flatSlices = computed(() => groupedSlices.value.flatMap((g) => g.items));
const activeItem = computed(() => flatSlices.value.find((el) => el.info.id === props.activeElement));

const listGroups = computed(() => groupedSlices.value.map((group) => ({
    id: group.id,
    text: group.text,
    glyph: group.glyph,
    items: group.items.map((item) => ({
        id: getSliceId(item),
        label: item.info.caption,
        href: props.type === 'links' ? (item.info.link || `#${getSliceId(item)}`) : undefined,
        tag: props.type === 'links' ? 'a' : 'button',
        counter: hasCounter(item) ? item?.data?.cards?.length : null,
        raw: item,
    })),
})));

/**
 * IMPORTANT: global detection for "all docs" aggregate combined mode.
 * If ANY parent has children with empty captions => never show children anywhere.
 */
const isAggregateCombined = computed(() => {
    if (props.type !== 'combined') return false;

    return (props.slices || []).some((parent) => {
        const children = parent?.slices || [];
        return children.length > 1 && children.every((c) => !c?.info?.caption);
    });
});

const combinedSelectGroups = computed(() => {
    if (props.type !== 'combined') return [];

    const map = new Map();
    (props.groups || []).forEach((g) => map.set(g.id, { ...g, items: [] }));

    const ungrouped = { id: '__ungrouped', text: null, glyph: null, items: [] };
    (props.slices || []).forEach((parent) => {
        const children = Array.isArray(parent?.slices) ? parent.slices : [];
        const groupId = parent?.groupId || children.find((c) => c?.groupId)?.groupId;

        if (groupId && map.has(groupId)) map.get(groupId).items.push(parent);
        else ungrouped.items.push(parent);
    });

    if (ungrouped.items.length) map.set('__ungrouped', ungrouped);

    return Array.from(map.values()).filter((g) => g.items?.length);
});

watch(
    () => props.activeElement,
    (val) => {
        combinedMobileValue.value = val;
    },
    { immediate: true },
);

onMounted(() => {
    isMounted.value = true;
});

function normalizeId(id) {
    return String(id || '')
        .replace(/^#/, '')
        .trim();
}

function getSliceId(item) {
    return normalizeId(item?.info?.id);
}

function hasCounter(item) {
    return item?.data?.counter;
}

function getClassElement(item) {
    return {
        's-side-menu-control__element_with-counter': hasCounter(item),
        's-side-menu-control__element_active': getSliceId(item) === props.activeElement,
    };
}

function showChildren(parent) {
    return !isAggregateCombined.value && (parent?.slices || []).length > 1;
}

function onChange(event) {
    const normalizedEvent = normalizeId(event);
    const index = (props.slices || []).findIndex(
        (item) => getSliceId(item) === normalizedEvent,
    );
    if (index === -1) return;

    const active = props.slices[index];

    if (active?.info?.link) {
        window.location.assign(active.info.link);
        return;
    }

    const info = active?.info || {};

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: 'Acronis',
        eventCategory: 'Interactions',
        eventAction: 'Side Menu',
        eventLabel: info.caption || '',
        eventContent: info.caption || '',
        eventLocation: getElementPath(menu.value?.children?.[0]),
        eventButton: (index + 1).toString(),
        ...info.event,
    });

    emit('tab-change', normalizedEvent);
}

function getCombinedTargetByAnyId(anyId) {
    const id = normalizeId(anyId);
    const parent = (props.slices || []).find((p) => getSliceId(p) === id);
    if (parent) return { parentId: id, childId: null };

    const parentByChild = (props.slices || []).find(
        (p) => Array.isArray(p?.slices) && p.slices.some((c) => getSliceId(c) === id),
    );

    return parentByChild
        ? { parentId: getSliceId(parentByChild), childId: id }
        : { parentId: id, childId: null };
}

function onCombinedMobileChange(value) {
    const { parentId, childId } = getCombinedTargetByAnyId(value);

    emit('tab-change', parentId, undefined, {
        scrollToId: childId || parentId,
        shouldScroll: true,
        allowSameId: true,
        syncHash: false,
    });
}

function onCombinedDesktopChildClick(parent, child) {
    const parentId = getSliceId(parent);
    const childId = getSliceId(child);

    emit('tab-change', parentId, undefined, {
        scrollToId: childId,
        shouldScroll: true,
        allowSameId: true,
        syncHash: false,
    });
}
</script>

<style lang="postcss" scoped>
.s-side-menu-control {
  background: white;
  border: 1px solid rgba(26, 96, 179, 0.15);
  border-radius: 4px;

  @media (--viewport-desktop) {
    top: 88px;
    max-height: calc(100vh - 88px);
    border: none;
    overflow-y: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    background: rgba(255, 255, 255, 0);
  }

  &--sticky {
    position: sticky;
    top: 20px;
  }

  :deep(.el-select-group) {
    &__title {
      @mixin note-accent;
      padding: 8px 20px 8px;
      color: var(--av-brand-primary);
      text-transform: uppercase;
    }

    .child-option {
      padding-inline-start: 40px;
    }
  }

  :deep(.el-select-group__wrap.is-visible) {
    &:has(+ .el-select-group__wrap.is-visible) {
      margin-bottom: 8px;
      padding-bottom: 8px;
    }
  }

  &__list {
    display: none;

    @media (--viewport-desktop) {
      display: block;
    }
  }

  &__select {
    display: block;

    :deep(.el-input) {
      &__icon {
        color: #024aa2 !important;
      }

      &__editor--absolute-position {
        color: var(--av-nav-primary);
        font-size: 16px;
      }
    }

    @media (--viewport-desktop) {
      display: none !important;
    }
  }

  &__arrow {
    margin-inline-end: 11px;
    fill: var(--av-nav-primary);
    transform: rotate(-90deg);
    transition: transform 0.3s ease-out;
  }

  &__sublist {
    padding: 16px 40px 16px;
  }

  &__subitem {
    &:not(:last-of-type) {
      margin-bottom: 16px;
    }
  }

  &__link {
    color: var(--av-nav-primary);

    &:hover {
      color: var(--av-nav-secondary);
    }
  }

  &__combined-wrapper {
    &--aggregate {
      display: none;
    }

    :deep(.s-side-menu-control) {
      &__select {
        @media (--viewport-desktop) {
          display: none;
        }
      }

      &__list {
        display: none;

        @media (--viewport-desktop) {
          display: block;
        }
      }
    }
  }

  :deep(.a-accordion-item) {
    border: 0;

    .title {
      padding: 0;
    }

    .content {
      @mixin paragraph;
    }

    .content-wrapper {
      padding: 0;
    }

    &.active {
      .s-side-menu-control__arrow {
        transform: rotate(0);
      }
    }
  }

  /* Shared extracted + parent-rendered styles */

  :deep(.s-side-menu-control__group) {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    gap: 8px;

    &:not(:first-child) {
      padding-top: 32px;
    }
  }

  :deep(.s-side-menu-control__group-icon) {
    max-width: 16px;
    max-height: 16px;

    &:not(.a-glyph_noFill) {
      fill: var(--av-brand-primary);
    }
  }

  :deep(.s-side-menu-control__group-title) {
    @mixin note-accent;
    max-width: calc(100% - 24px);
    text-transform: uppercase;
    color: var(--av-brand-primary);
  }

  :deep(.s-side-menu-control__element) {
    @mixin paragraph;

    display: flex;
    align-items: center;
    width: 100%;
    padding: 12px 16px;
    border: none;
    border-radius: 4px;
    background: none;
    text-align: start;
    text-decoration: none;
    color: var(--av-nav-primary);
    cursor: pointer;

    &:hover {
      background-color: var(--av-brand-lightest);
      color: var(--av-nav-primary);
    }
  }

  :deep(.s-side-menu-control__element_active) {
    background-color: var(--av-brand-accent);
  }

  :deep(.s-side-menu-control__element_with-counter) {
    justify-content: space-between;
  }

  :deep(.qa-input-select-suffix) {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  :deep(.s-side-menu-control__element-counter) {
    @mixin body;
    font-weight: 400;
    color: var(--av-fixed-light);

    &.select-counter {
      @mixin paragraph;
      margin-inline-start: auto;
      color: var(--av-fixed-primary);
    }
  }
}
</style>
